/*
 * Charts as hand-written SVG.
 *
 * A charting library would add ~150 KB gzipped to serve two shapes. These render from the
 * same data the API already returns, inherit the theme through currentColor, and have no
 * layout thrash on resize because they scale with viewBox rather than measuring the DOM.
 */

import { useMemo } from 'react'

export interface Series {
  key: string
  label: string
  colour: string
  values: number[]
}

/**
 * Stacked bars: delivered / bounced / deferred per day.
 *
 * Stacked rather than grouped because the question a sender actually asks is "of what I
 * sent that day, how much landed" — a proportion, which grouped bars make you compute by
 * eye.
 */
export function StackedBars({
  labels, series, height = 180,
}: { labels: string[]; series: Series[]; height?: number }) {
  const n = labels.length
  const totals = useMemo(
    () => labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0)),
    [labels, series])

  const max = Math.max(1, ...totals)

  if (n === 0 || max <= 1) {
    return (
      <div className="chart-empty">
        No delivery data for this period yet. It appears here as soon as the first message
        is accepted.
      </div>
    )
  }

  // A y-axis scale plus full-width gridlines makes the plot read as a real analytics chart
  // even with only a few bars — the structure fills the frame instead of leaving it empty.
  const ticks = [1, 0.75, 0.5, 0.25, 0].map(f => Math.round(max * f))
  return (
    <div className="chart">
      <div className="chart-plot" style={{ height }}>
        <div className="y-axis">
          {ticks.map((t, i) => <span key={i}>{t.toLocaleString()}</span>)}
        </div>
        <div className="sbars">
          {[1, 0.75, 0.5, 0.25, 0].map(f => (
            <div className="sbar-grid" key={f} style={{ bottom: `${f * 100}%` }} />
          ))}
          {/* Trend line over the bars: a smooth polyline through each day's total, with a
              soft gradient area beneath — the "line chart" view layered on the histogram. */}
          {n > 1 && (
            <svg className="sbar-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              <defs>
                <linearGradient id="iqTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand-500)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--brand-500)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {(() => {
                const pts = totals.map((t, i) => {
                  const x = n === 1 ? 50 : (i / (n - 1)) * 100
                  const y = 100 - (t / max) * 100
                  return [x, y] as const
                })
                const line = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
                const area = `0,100 ${line} 100,100`
                return (
                  <>
                    <polygon points={area} fill="url(#iqTrendFill)" />
                    <polyline points={line} fill="none" stroke="var(--brand-500)" strokeWidth="0.9"
                              strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                    {pts.map(([x, y], i) => (
                      <circle key={i} cx={x} cy={y} r="1.1" fill="var(--brand-600)" vectorEffect="non-scaling-stroke" />
                    ))}
                  </>
                )
              })()}
            </svg>
          )}
          {labels.map((label, i) => {
            const total = totals[i] ?? 0
            const colHeight = (total / max) * 100
            return (
              <div className="sbar-col" key={label} title={`${label}: ${total.toLocaleString()}`}>
                {total > 0 && <span className="sbar-val" style={{ bottom: `calc(${colHeight}% + 4px)` }}>{total.toLocaleString()}</span>}
                <div className="sbar-stack" style={{ height: `${colHeight}%` }}>
                  {series.map(s => {
                    const v = s.values[i] ?? 0
                    if (v <= 0 || total <= 0) return null
                    return (
                      <div key={s.key} className="sbar-seg"
                           style={{ height: `${(v / total) * 100}%`, background: s.colour }}
                           title={`${label} · ${s.label}: ${v.toLocaleString()}`} />
                    )
                  })}
                </div>
                <span className="sbar-x">{label}</span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="legend">
        {series.map(s => (
          <span key={s.key}>
            <i style={{ background: s.colour }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * A single rate with its own warning thresholds.
 *
 * The thresholds come from the API rather than being hard-coded here, because the numbers
 * that matter are set by Gmail and Yahoo, not by us — and when they change, one server-side
 * constant should be enough.
 */
export function RateCard({
  label, value, warn, critical, hint, invert = false,
}: {
  label: string
  value: number | null
  warn: number
  critical: number
  hint: string
  /** true when HIGHER is worse (bounce, complaint); false when higher is better. */
  invert?: boolean
}) {
  const state = value === null ? 'none'
    : invert
      ? (value >= critical ? 'critical' : value >= warn ? 'warn' : 'ok')
      : (value <= critical ? 'critical' : value <= warn ? 'warn' : 'ok')

  return (
    <div className={`rate ${state}`}>
      <div className="rate-label">{label}</div>
      <div className="rate-value">
        {value === null ? <span className="muted">—</span> : `${value}%`}
      </div>
      <div className="rate-bar">
        <span style={{ width: `${Math.min(100, value ?? 0)}%` }} />
      </div>
      <div className="rate-hint">{hint}</div>
    </div>
  )
}

// ==================================================================== distributions

/**
 * A horizontal labelled bar list — for "tenants by plan", "by status" and similar
 * breakdowns. Each row's bar is proportional to the largest value, so the shape reads at a
 * glance without axes.
 */
export function BarList({
  items,
}: { items: { label: string; value: number; colour?: string }[] }) {
  const max = Math.max(1, ...items.map(i => i.value))
  if (items.length === 0) return <div className="chart-empty">No data yet.</div>
  return (
    <div className="barlist">
      {items.map((it) => (
        <div className="barlist-row" key={it.label}>
          <span className="barlist-label">{it.label}</span>
          <span className="barlist-track">
            <span className="barlist-fill"
                  style={{ width: `${(it.value / max) * 100}%`, background: it.colour ?? 'var(--brand-500)' }} />
          </span>
          <span className="barlist-value">{it.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * A donut for a small categorical split. Pure SVG stroke-dasharray arcs — no library, and
 * it scales cleanly. Deliberately capped at a handful of slices; more than that belongs in
 * a BarList, which stays readable.
 */
export function Donut({
  items, size = 150,
}: { items: { label: string; value: number; colour: string }[]; size?: number }) {
  const total = items.reduce((s, i) => s + i.value, 0)
  const r = size / 2 - 12
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div className="donut">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Distribution">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {total === 0
            ? <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth="14" />
            : items.map((it) => {
                const frac = it.value / total
                const dash = frac * c
                const el = (
                  <circle key={it.label} cx={size / 2} cy={size / 2} r={r} fill="none"
                          stroke={it.colour} strokeWidth="14"
                          strokeDasharray={`${dash} ${c - dash}`} strokeDashoffset={-offset} />
                )
                offset += dash
                return el
              })}
        </g>
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
              fontSize="26" fontWeight="700" fill="var(--text-1)">{total}</text>
      </svg>
      <div className="donut-legend">
        {items.map((it) => (
          <span key={it.label}><i style={{ background: it.colour }} />{it.label} <b>{it.value}</b></span>
        ))}
      </div>
    </div>
  )
}
